import React, { useRef, useCallback } from 'react'
import html2canvas from 'html2canvas'
import { Annotation } from './types'

interface ScreenshotCaptureProps {
  targetElement?: HTMLElement | null
  annotations: Annotation[]
  onScreenshotTaken: (dataUrl: string, annotations: Annotation[]) => void
  className?: string
  children?: React.ReactNode
}

export const ScreenshotCapture: React.FC<ScreenshotCaptureProps> = ({
  targetElement,
  annotations,
  onScreenshotTaken,
  className = '',
  children
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  // Auto-capture functionality handled elsewhere
  React.useEffect(() => {
    // Component renders but screenshot logic is in useScreenshot hook below
  }, [targetElement, annotations, onScreenshotTaken])

  return (
    <div ref={containerRef} className={className}>
      {children}
      {/* Function available but not exposed in UI for now */}
    </div>
  )
}

// Hook for screenshot functionality
export const useScreenshot = () => {
  const takeElementScreenshot = useCallback(async (
    element: HTMLElement,
    options: {
      scale?: number
      backgroundColor?: string
      width?: number
      height?: number
    } = {}
  ): Promise<string> => {
    const {
      scale = 1,
      backgroundColor = '#ffffff',
      width,
      height
    } = options

    const canvas = await html2canvas(element, {
      useCORS: true,
      allowTaint: true,
      scale,
      backgroundColor,
      width,
      height,
      logging: false
    })

    return canvas.toDataURL('image/png')
  }, [])

  const takeVideoFrameScreenshot = useCallback(async (
    video: HTMLVideoElement
  ): Promise<string> => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    
    if (!ctx) {
      throw new Error('Could not get canvas context')
    }

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    
    return canvas.toDataURL('image/png')
  }, [])

  const downloadScreenshot = useCallback((dataUrl: string, filename: string = 'screenshot.png') => {
    const link = document.createElement('a')
    link.download = filename
    link.href = dataUrl
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [])

  return {
    takeElementScreenshot,
    takeVideoFrameScreenshot,
    downloadScreenshot
  }
}